import React, { useEffect, useRef, useState, useCallback } from "react";
import { Trophy, Sparkles, ChevronLeft, ChevronRight, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { onWorkspaceChange } from "@/lib/active-workspace";

/**
 * MilestoneFeed - horizontal, snap-scrolling reel of the user's past career
 * milestones (resume optimizations, ATS ready, interview complete, offer
 * ready). Purely celebratory & motivational; not part of any scoring
 * pipeline. Fetches from `/api/dashboard/milestones`. Renders nothing when
 * the user has zero milestones yet, so the Dashboard stays tidy for new
 * accounts.
 *
 * Accent → color mapping is intentionally aligned with CareerMilestoneBanner
 * so users see the same visual language whether it's a live celebration
 * banner or a memory in the reel.
 */
const ACCENT = {
  gold:    { border: "border-amber-500/35",   bg: "bg-amber-500/[0.06]",   text: "text-amber-300",   icon: "text-amber-400" },
  green:   { border: "border-emerald-500/35", bg: "bg-emerald-500/[0.06]", text: "text-emerald-300", icon: "text-emerald-400" },
  emerald: { border: "border-emerald-500/35", bg: "bg-emerald-500/[0.06]", text: "text-emerald-300", icon: "text-emerald-400" },
};

function accent(name) {
  return ACCENT[name] || ACCENT.gold;
}

function formatAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function MilestoneCard({ m }) {
  const acc = accent(m.accent);
  const primary = m.primary || {};
  const secondary = m.secondary;
  const showDelta = typeof primary.delta === "number" && primary.delta > 0;
  return (
    <div
      data-testid={`milestone-card-${m.kind}`}
      className={`relative flex-shrink-0 w-64 rounded-xl border ${acc.border} ${acc.bg} p-4 snap-start transition-all hover:-translate-y-0.5 hover:shadow-[0_0_28px_-8px_rgba(245,158,11,0.35)]`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${acc.text}`}>
          {m.kind === "interview_complete" ? (
            <Trophy className={`h-3 w-3 ${acc.icon}`} />
          ) : (
            <Sparkles className={`h-3 w-3 ${acc.icon}`} />
          )}
          {m.label || "Milestone"}
        </div>
        <span className="text-[10px] text-zinc-500 tabular-nums">{formatAgo(m.achieved_at)}</span>
      </div>

      <div className="text-sm font-semibold text-zinc-50 leading-snug">{m.headline}</div>

      {(m.role || m.company) && (
        <div className="text-[11px] text-zinc-400 mt-1 truncate">
          {[m.role, m.company].filter(Boolean).join(" @ ")}
        </div>
      )}

      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-2xl font-bold text-zinc-50 tabular-nums">
          {primary.after}
          <span className="text-xs font-medium text-zinc-500 ml-0.5">{primary.unit || ""}</span>
        </span>
        {showDelta && (
          <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-400 tabular-nums">
            <TrendingUp className="h-3 w-3" />
            +{primary.delta}
          </span>
        )}
        {typeof primary.before === "number" && !showDelta && (
          <span className="text-[11px] text-zinc-500 tabular-nums">
            from {primary.before}{primary.unit || ""}
          </span>
        )}
      </div>
      {secondary && (
        <div className="mt-1 text-[11px] text-zinc-400">
          {secondary.label}: <span className="text-zinc-200 font-medium tabular-nums">{secondary.after}{secondary.unit || ""}</span>
        </div>
      )}
    </div>
  );
}

export default function MilestoneFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const scrollerRef = useRef(null);

  const fetchIt = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/milestones");
      setItems(data?.milestones || []);
    } catch (e) {
      console.debug("[MilestoneFeed] fetch failed", e?.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIt();
    return onWorkspaceChange(() => fetchIt());
  }, [fetchIt]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 280, behavior: "smooth" });
  };

  if (loading || items.length === 0) return null;

  // Counts per kind drive the chip labels. `all` is always present; per-kind
  // chips only render when the user actually has milestones of that kind so
  // the UI never shows an empty filter.
  const counts = items.reduce((acc, m) => {
    acc.all += 1;
    if (m.kind === "resume_optimized") acc.resume += 1;
    else if (m.kind === "ats_ready") acc.ats += 1;
    else if (m.kind === "interview_complete") acc.interview += 1;
    return acc;
  }, { all: 0, resume: 0, ats: 0, interview: 0 });

  const chips = [
    { id: "all", label: "All" },
    { id: "resume", label: "Resume", kind: "resume_optimized" },
    { id: "ats", label: "ATS", kind: "ats_ready" },
    { id: "interview", label: "Interview", kind: "interview_complete" },
  ].filter((c) => counts[c.id] > 0);

  const filtered = filter === "all"
    ? items
    : items.filter((m) => (
      (filter === "resume" && m.kind === "resume_optimized") ||
      (filter === "ats" && m.kind === "ats_ready") ||
      (filter === "interview" && m.kind === "interview_complete")
    ));

  return (
    <section
      data-testid="milestone-feed"
      className="relative rounded-2xl border border-zinc-800/70 bg-zinc-950/40 p-5 sm:p-6"
    >
      <div className="flex items-end justify-between mb-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-1 inline-flex items-center gap-1.5">
            <Trophy className="h-3 w-3" /> Progress Highlights
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-zinc-50">Milestones you&apos;ve earned</h2>
          <div className="text-xs text-zinc-500 mt-0.5">
            {filtered.length} of {items.length} · newest first
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5">
          <button
            onClick={() => scrollBy(-1)}
            data-testid="milestone-feed-prev"
            className="h-8 w-8 flex items-center justify-center rounded-full border border-zinc-800 text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 transition-colors"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => scrollBy(1)}
            data-testid="milestone-feed-next"
            className="h-8 w-8 flex items-center justify-center rounded-full border border-zinc-800 text-zinc-400 hover:text-amber-300 hover:border-amber-500/40 transition-colors"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filter chips, only rendered when the user has at least 2 kinds of
          milestones (otherwise filtering adds no value). */}
      {chips.length > 2 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="milestone-feed-filters">
          {chips.map((c) => {
            const active = filter === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                data-testid={`milestone-filter-${c.id}`}
                className={`inline-flex items-center gap-1.5 text-[11px] rounded-full border px-2.5 py-1 transition-colors ${
                  active
                    ? "bg-amber-500/[0.12] border-amber-500/40 text-amber-200"
                    : "border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700"
                }`}
              >
                {c.label}
                <span className={`tabular-nums text-[10px] ${active ? "text-amber-300" : "text-zinc-500"}`}>
                  {counts[c.id]}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 [scrollbar-width:thin]"
        style={{ scrollbarColor: "rgb(63 63 70) transparent" }}
      >
        {filtered.map((m) => (
          <MilestoneCard key={m.key} m={m} />
        ))}
      </div>
    </section>
  );
}
